import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AtomAssistLogo from "src/views/assistant/components/AssistantLogo";
import AAAssistantText from "src/views/assistant/components/AAAssistantText";
import AtomAssistTypingIndicator from "src/reusable/Chat/ChatMessage/AtomAssistTypingIndicator";
import ChatContainer from "src/reusable/Chat/ChatContainer";
import { ChatStreamProvider } from "src/reusable/Chat/ChatStreaming/context/ChatStreamContext";
import {
  ChatWrapperContainer,
  PromptContainer,
} from "src/reusable/Chat/StyledContainers/StyledContainers";
import ProposalGenerationChatInput from "./components/ProposalGenerationChatInput.tsx";
import ProposalGenerationHeader from "./components/ProposalGenerationHeader.tsx";
import { StageApprovalModal } from "./components/StageApprovalModal.tsx";
import { ProposalGenerationSidebar } from "./components/sidebar/ProposalGenerationSidebar";
import {
  useGetProposalGenerationDialogueData,
  useCreateStages,
  useIdentifyStages,
  fetchEnrichedArtifacts,
  IdentifiedStagesResponse,
} from "./services/ProposalGenerationServices";
import { useGetStageConfig } from "./services/ProposalGenerationConfigService";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import { getMultipleDatasets } from "src/views/datasets/services/DatasetServices";
import {
  Box,
  ButtonBase,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { useProposalGenerationStore } from "./store/ProposalGenerationStore";
import { useProposalGenerationChat } from "./hooks/useProposalGenerationChat";
import {
  buildMessageTree,
  getLastAssistantMessage,
} from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import {
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "src/views/assistant/stores/ChatStore";
import useLoadDialogue from "src/views/assistant/hooks/useLoadDialogue";
import useStartChatOnMount from "src/reusable/Chat/hooks/useStartChatOnMount";
import { useHistory } from "react-router-dom";
import { NucToast } from "src/reusable/components/Toast";
import { PROPOSAL_GENERATION_PRODUCT } from "./proposalGenerationProduct";
import { useWorkflowDialoguePersona } from "src/views/products/workflow/hooks/useWorkflowLeadPersona";

const ProposalGenerationTypingIndicator = (
  props: ComponentProps<typeof AtomAssistTypingIndicator>,
) => (
  <Box data-testid="propgen-thinking-indicator" sx={{ display: "contents" }}>
    <AtomAssistTypingIndicator {...props} />
  </Box>
);

const ProposalGenerationChat = ({
  // @ts-ignore: TS6133 is declared but its value is never read
  proposalGenerationRunId,
  dialogueId,
  initialUserMessage,
}: {
  proposalGenerationRunId: number | undefined;
  dialogueId: number | undefined;
  initialUserMessage?: string;
}) => {
  const history = useHistory();
  const theme = useTheme();

  // Use ChatStore - single source of truth (matching AtomAssist pattern)
  const {
    config: chatConfig,
    setActiveDialogueId,
    setDropdownDatasets,
    setDialogue: setStoreDialogue,
    setInitialUserMessage,
  } = useChatStore();

  // Subscribe to dropdownDatasets length so the dataset-loading effect can
  // self-heal when setConfig (called by useLoadDialogue) overwrites them.
  const dropdownDatasetsLength = useChatStore(
    (state) => state.dropdownDatasets?.length ?? 0,
  );

  // Get active dataset IDs from store's computed selector
  const activeDatasetIds = useActiveDatasetIds();

  // Get source data record IDs for document-scoped search
  const sourceDataRecordIds = useSourceDataRecordIds();

  // Track previous dialogueId to detect stage navigation
  const prevDialogueIdRef = useRef<number | undefined>(undefined);

  // Update active dialogue ID in store and clear stale sidebar data on stage change
  useEffect(() => {
    if (dialogueId) {
      setActiveDialogueId(dialogueId);

      // When navigating between stages (dialogueId changes), clear sidebar data
      // so it gets reloaded from the new dialogue's backend data.
      // Skip on initial mount (prevDialogueIdRef is undefined) to preserve
      // data set by the splash page before navigation.
      if (
        prevDialogueIdRef.current !== undefined &&
        prevDialogueIdRef.current !== dialogueId
      ) {
        console.log(
          "[ProposalGenerationChat] Dialogue changed from",
          prevDialogueIdRef.current,
          "to",
          dialogueId,
          "- clearing sidebar data for reload",
        );
        useProposalGenerationStore.getState().setInputFiles([]);
        setDropdownDatasets([]);
        // Persona choices are per-dialogue: without this, the persona selected
        // (or auto-selected) for the review kickoff rides the persisted store
        // into stage dialogues and its chat_config_id overrides the stage's
        // own creation-time persona. useWorkflowDialoguePersona below refills
        // with the new dialogue's own resolution.
        useChatStore.getState().resetToBaseConfig();
      }
      prevDialogueIdRef.current = dialogueId;
    }
  }, [dialogueId, setActiveDialogueId, setDropdownDatasets]);

  // Update dropdown datasets from config - BUT preserve datasets set from splash page
  useEffect(() => {
    const currentDropdownDatasets = useChatStore.getState().dropdownDatasets;
    const hasExistingDatasets =
      currentDropdownDatasets && currentDropdownDatasets.length > 0;

    // Only set from chatConfig if we don't already have datasets (from splash page)
    if (chatConfig?.datasets && !hasExistingDatasets) {
      setDropdownDatasets(chatConfig.datasets);
    }
  }, [chatConfig?.datasets, setDropdownDatasets]);

  // Select this dialogue's backend-resolved persona whenever none is set —
  // after the hop reset above, each stage chat shows and sends the persona
  // that would actually run it; a manual pick still wins.
  useWorkflowDialoguePersona(PROPOSAL_GENERATION_PRODUCT, dialogueId);

  // Load existing dialogue and reconstruct artifacts
  const { dialogue: loadedDialogue } = useLoadDialogue({
    initialUserMessage,
  });

  // Fetch ProposalGenerationDialogueData to get inherited datasets for generated stages
  const { data: proposalGenDialogueData, refetch: refetchProposalData } =
    useGetProposalGenerationDialogueData(dialogueId);

  // Keep the store's report_type / sequence_number in sync with the open
  // dialogue so the header chip reflects the chat actually opened.
  useEffect(() => {
    if (!proposalGenDialogueData) return;
    const { setReportType, setSequenceNumber } =
      useProposalGenerationStore.getState();
    setReportType(proposalGenDialogueData.report_type ?? null);
    setSequenceNumber(proposalGenDialogueData.sequence_number ?? null);
  }, [
    proposalGenDialogueData?.report_type,
    proposalGenDialogueData?.sequence_number,
  ]);

  // Load datasets from ProposalGenerationDialogueData when entering chat directly
  // or when navigating between stages (data is cleared by the dialogueId change effect above)
  // Also re-runs when dropdownDatasetsLength changes (e.g. setConfig in useLoadDialogue
  // overwrites dropdownDatasets after this effect already skipped).
  useEffect(() => {
    let isMounted = true;

    const loadDatasetsFromArtifacts = async () => {
      const datasetIds = proposalGenDialogueData?.dataset_ids;

      if (datasetIds && datasetIds.length > 0) {
        // Skip fetch if store already has the correct datasets for this dialogue
        const currentDatasets = useChatStore.getState().dropdownDatasets;
        const currentIds = new Set(currentDatasets?.map((d) => d.id));
        const allPresent = datasetIds.every((id: number) => currentIds.has(id));
        if (allPresent && currentDatasets?.length === datasetIds.length) {
          return; // Already loaded the correct datasets
        }

        console.log(
          "[ProposalGenerationChat] Fetching datasets for dialogue:",
          datasetIds,
        );
        try {
          const fetchedDatasets = await getMultipleDatasets(datasetIds);
          if (isMounted && fetchedDatasets && fetchedDatasets.length > 0) {
            setDropdownDatasets(fetchedDatasets);
          } else if (!isMounted) {
            console.log(
              "[ProposalGenerationChat] Component unmounted, skipping dataset update",
            );
          } else {
            console.warn(
              "[ProposalGenerationChat] No datasets found for IDs:",
              datasetIds,
            );
          }
        } catch (error) {
          if (isMounted) {
            console.error(
              "[ProposalGenerationChat] Failed to fetch datasets:",
              error,
            );
          }
        }
      }
    };

    loadDatasetsFromArtifacts();

    return () => {
      isMounted = false;
    };
  }, [
    proposalGenDialogueData?.dataset_ids,
    dialogueId,
    dropdownDatasetsLength,
    setDropdownDatasets,
  ]);

  // Load input documents from backend when navigating to an existing chat
  // or when navigating between stages (data is cleared by the dialogueId change effect above)
  useEffect(() => {
    let isMounted = true;

    const loadInputDocuments = async () => {
      const artifactIds = proposalGenDialogueData?.input_artifact_ids;

      if (artifactIds && artifactIds.length > 0) {
        // Skip fetch if store already has the correct documents for this dialogue
        const currentRfpFiles =
          useProposalGenerationStore.getState().inputFiles;
        const currentIds = new Set(currentRfpFiles.map((f) => f.id));
        const allPresent = artifactIds.every((id: number) =>
          currentIds.has(id),
        );
        if (allPresent && currentRfpFiles.length === artifactIds.length) {
          return; // Already loaded the correct documents
        }
        try {
          const enrichedArtifacts = await fetchEnrichedArtifacts(artifactIds);
          if (isMounted && enrichedArtifacts.length > 0) {
            const inputFiles = enrichedArtifacts.map((artifact) => ({
              name: cleanArtifactFilename(artifact.filename),
              size: artifact.filesize || 0,
              id: artifact.id,
              datasetId: artifact.dataset_id,
              sdrId: artifact.sdr_id,
              uploadProgress: 100,
              processingProgress: 100,
              uploadStatus: "uploaded" as const,
              processingStatus: "completed" as const,
            }));
            useProposalGenerationStore.getState().setInputFiles(inputFiles);
          }
        } catch (error) {
          if (isMounted) {
            console.error(
              "[ProposalGenerationChat] Failed to fetch input documents:",
              error,
            );
          }
        }
      }
    };

    loadInputDocuments();

    return () => {
      isMounted = false;
    };
  }, [proposalGenDialogueData?.input_artifact_ids, dialogueId]);

  // Memoize dialogue tree building
  const dialogueTree = useMemo(() => {
    if (
      loadedDialogue &&
      loadedDialogue.messages &&
      loadedDialogue.messages.length > 0
    ) {
      return buildMessageTree(loadedDialogue.messages);
    }
    return [];
  }, [loadedDialogue]);

  // Build datasets for streaming from store's computed selector
  const datasetsToUse = useMemo(
    () => activeDatasetIds.map((id) => ({ id })),
    [activeDatasetIds],
  );

  const chatConfigMemo = useMemo(() => {
    return {
      dialogueId,
      modelId: chatConfig?.modelId,
      modelVersionNum: chatConfig?.modelVersionNum,
      datasetsForSearchContext: datasetsToUse,
      sourceDataRecordIds:
        sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
      numSearchResults: chatConfig?.topSearchResults || 5,
      textGenModelParams: chatConfig?.chatControlMetadata,
      systemPrompt: chatConfig?.systemPrompt,
      toolCalling: true,
      chatConfigId: chatConfig?.chatConfigId,
      product: "proposal-generation",
    };
  }, [
    dialogueId,
    chatConfig?.modelId,
    chatConfig?.modelVersionNum,
    chatConfig?.chatControlMetadata,
    chatConfig?.systemPrompt,
    chatConfig?.chatConfigId,
    chatConfig?.topSearchResults,
    datasetsToUse,
    sourceDataRecordIds,
  ]);

  const {
    dialogue,
    responseSources,
    setResponseSources,
    terminateConnection,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
    isMessageStreamStopped: isChatMessageStreamStopped,
    isTurnActive,
    emptySubmission,
    clearChatErrors,
    sendUserMessage,
    resendUserMessage,
    regenerateResponse,
    setAsPreferredMessage,
    setDialogue,
    reconstructArtifactsFromMessages,
    reset: resetChatStream,
    tokenUsage,
    setTokenUsage,
    retryConnection,
    lastDialogueId,
    activeSubAgents,
    store: chatStore,
  } = useProposalGenerationChat(chatConfigMemo, dialogueTree);

  // Load existing dialogue and reconstruct artifacts when component mounts
  // Following AtomAssist pattern - proper useEffect dependencies, React handles deduplication
  useEffect(() => {
    if (dialogueTree.length > 0) {
      setDialogue(dialogueTree);
      setStoreDialogue(dialogueTree);

      // Reconstruct artifacts from message content (ArtifactSlice version handles tool_call/tool_response)
      reconstructArtifactsFromMessages(dialogueTree);
    }
  }, [
    dialogueTree.length,
    dialogueId,
    setDialogue,
    setStoreDialogue,
    reconstructArtifactsFromMessages,
  ]);

  // Seed token usage from loaded dialogue config (for context window gauge on history load)
  useEffect(() => {
    if (loadedDialogue?.config?.token_usage) {
      setTokenUsage(loadedDialogue.config.token_usage);
    }
  }, [loadedDialogue?.config?.token_usage, dialogueId, setTokenUsage]);

  // calculate last assistant message for sidebar
  const lastAssistantMessage = useMemo(
    () => getLastAssistantMessage(dialogue),
    [dialogue?.length, dialogue?.[dialogue?.length - 1]?.id],
  );

  // Get store state for multi-stage workflow
  const {
    runId: storeRunId,
    reportSdrId,
    showStageApprovalModal,
    setShowStageApprovalModal,
    identifiedStages,
    setIdentifiedStages,
  } = useProposalGenerationStore();

  // Effective stage config (labels/nouns for toasts; defaults reproduce legacy UX)
  const { data: stageConfig } = useGetStageConfig();
  const stageNounPlural =
    stageConfig?.effective.stage_noun_plural || "disciplines";

  // Hook for identifying stages
  const { mutate: identifyStagesMutation, isLoading: isIdentifyingStages } =
    useIdentifyStages();

  // Handler for identify stages button
  const handleIdentifyStages = useCallback(() => {
    const runId = proposalGenerationRunId || storeRunId;
    if (!runId || !reportSdrId) {
      NucToast({
        message: "Missing run ID or report ID",
        type: "error",
      });
      return;
    }

    identifyStagesMutation(
      {
        runId,
      },
      {
        onSuccess: (data: IdentifiedStagesResponse) => {
          console.log("Identified stages:", data.stages);

          // Set identified stages in store
          setIdentifiedStages({
            stages: data.stages,
            reviewReportSdrId: data.review_report_sdr_id,
          });

          // Show approval modal
          setShowStageApprovalModal(true);

          NucToast({
            message: `Identified ${data.stages.length} ${stageNounPlural}`,
            type: "success",
          });
        },
        onError: (error: any) => {
          console.error("Failed to identify stages:", error);
          NucToast({
            message: `Failed to identify ${stageNounPlural}. Please try again.`,
            type: "error",
          });
        },
      },
    );
  }, [
    proposalGenerationRunId,
    storeRunId,
    reportSdrId,
    identifyStagesMutation,
    setIdentifiedStages,
    setShowStageApprovalModal,
    stageNounPlural,
  ]);

  // Chat pane collapse state - allows user to minimize chat to focus on sidebar/reports
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);

  // useStartChatOnMount sends the initial message from the splash page
  // Pass clearInitialMessage to clear the message after sending (prevents duplicate on refresh)
  useStartChatOnMount({
    initialUserMessage,
    clearInitialMessage: () => setInitialUserMessage(undefined),
    emptySubmission,
    sendUserMessage,
  });

  // A new chat redirects to /proposal-generation
  const onNewChat = useCallback(() => {
    // Clear uploaded files before navigating to splash (matching AtomAssist pattern)
    useChatStore.getState().clearUploadedFiles();
    history.push("/proposal-generation");
  }, [history]);

  useEffect(() => {
    return () => {
      resetChatStream();
      terminateConnection();
      clearChatErrors();
    };
  }, [resetChatStream, terminateConnection, clearChatErrors]);

  return (
    <>
      <Box
        id="proposal-generation-header"
        sx={{
          height: "55px",
          bgcolor: "background.paper",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
        }}
      >
        <ProposalGenerationHeader
          dialogueId={dialogueId || null}
          onNewChat={onNewChat}
          dialogue={dialogue}
        />
      </Box>
      <ChatStreamProvider store={chatStore}>
        <Stack
          direction="row"
          sx={{
            height: "calc(100vh - 97px)",
            overflow: "hidden",
          }}
        >
          {/* Chat Pane - vertically collapsible (messages hide, input stays) */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {/* Messages area - collapses vertically */}
            <ChatWrapperContainer
              sx={{
                flex: isChatCollapsed ? "0 0 0px" : 1,
                overflow: isChatCollapsed ? "hidden" : undefined,
                transition: "flex 0.3s ease",
              }}
            >
              <ChatContainer
                activeSubAgents={activeSubAgents}
                dialogueId={dialogueId || undefined}
                dialogue={dialogue}
                isChatConnectionError={isChatConnectionError}
                chatStreamError={chatStreamError}
                isChatConnected={isChatConnected}
                isChatEnded={isChatEnded}
                isChatLoading={isChatLoading}
                isChatMessageStreamStopped={isChatMessageStreamStopped}
                startNewChat={onNewChat}
                resendUserMessage={resendUserMessage}
                regenerateResponse={regenerateResponse}
                setAsPreferredMessage={setAsPreferredMessage}
                onRetryConnection={lastDialogueId ? retryConnection : undefined}
                CustomLoadingIndicator={ProposalGenerationTypingIndicator}
                ChatPromptInput={
                  isChatCollapsed ? (
                    // When collapsed, input is rendered below the toggle bar instead
                    <></>
                  ) : (
                    <ProposalGenerationChatInput
                      chatStreamError={chatStreamError}
                      terminateConnection={terminateConnection}
                      sendUserMessage={sendUserMessage}
                      isChatConnectionError={isChatConnectionError}
                      isChatConnected={isChatConnected}
                      isChatLoading={isChatLoading}
                      isTurnActive={isTurnActive}
                      tokenUsage={tokenUsage}
                      proposalGenDialogueData={proposalGenDialogueData}
                      refetchProposalData={refetchProposalData}
                    />
                  )
                }
                BackgroundComponent={
                  <AtomAssistLogo
                    lowerMessage={"How can I help you with your proposal?"}
                  />
                }
                CustomAssistantText={AAAssistantText}
                CustomAssistantTextProps={{
                  isChatConnected,
                  responseSources,
                  onSourcesUpdate: setResponseSources,
                }}
              />
            </ChatWrapperContainer>

            {/* Collapse/expand toggle bar — full-width button for a generous
              click target while staying a single semantic <button>. */}
            <Tooltip
              title={
                isChatCollapsed
                  ? "Show chat messages"
                  : "Minimize chat messages"
              }
            >
              <ButtonBase
                onClick={() => setIsChatCollapsed((prev) => !prev)}
                aria-label={
                  isChatCollapsed
                    ? "Show chat messages"
                    : "Minimize chat messages"
                }
                aria-expanded={!isChatCollapsed}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 0.5,
                  width: "100%",
                  borderTop: `1px solid ${theme.palette.divider}`,
                  bgcolor: "background.paper",
                  color: "primary.main",
                  "&:hover": { bgcolor: "action.hover" },
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: -2,
                  },
                  flexShrink: 0,
                }}
              >
                {isChatCollapsed ? (
                  <UnfoldMoreIcon fontSize="small" />
                ) : (
                  <UnfoldLessIcon fontSize="small" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {isChatCollapsed ? "Show messages" : "Minimize messages"}
                </Typography>
              </ButtonBase>
            </Tooltip>

            {/* Chat input - always visible, rendered outside ChatContainer when collapsed */}
            {isChatCollapsed && (
              <PromptContainer>
                <ProposalGenerationChatInput
                  chatStreamError={chatStreamError}
                  terminateConnection={terminateConnection}
                  sendUserMessage={sendUserMessage}
                  isChatConnectionError={isChatConnectionError}
                  isChatConnected={isChatConnected}
                  isChatLoading={isChatLoading}
                  isTurnActive={isTurnActive}
                  tokenUsage={tokenUsage}
                  proposalGenDialogueData={proposalGenDialogueData}
                  refetchProposalData={refetchProposalData}
                />
              </PromptContainer>
            )}
          </Box>

          {/* Proposal Generation Sidebar (Sources, Artifacts, Report, Stages) */}
          <ProposalGenerationSidebar
            responseSources={responseSources}
            lastAssistantMessage={lastAssistantMessage}
            onIdentifyStages={handleIdentifyStages}
            isIdentifyingStages={isIdentifyingStages}
          />
        </Stack>
      </ChatStreamProvider>

      {/* Stage Approval Modal */}
      {identifiedStages && (proposalGenerationRunId || storeRunId) && (
        <ProposalGenerationStageApprovalWrapper
          open={showStageApprovalModal}
          onClose={() => setShowStageApprovalModal(false)}
          stages={identifiedStages.stages}
          reviewReportSdrId={identifiedStages.reviewReportSdrId}
          runId={(proposalGenerationRunId || storeRunId) as number}
          stageNounSingular={stageConfig?.effective.stage_noun_singular}
          stageNounPlural={stageConfig?.effective.stage_noun_plural}
          onSuccess={() => {
            setShowStageApprovalModal(false);
            setIdentifiedStages(null);
          }}
        />
      )}
    </>
  );
};

/**
 * ProposalGenerationStageApprovalWrapper - Adapter component
 * Wraps StageApprovalModal to handle the API call for creating generated stages
 */
interface ProposalGenerationStageApprovalWrapperProps {
  open: boolean;
  onClose: () => void;
  stages: string[];
  reviewReportSdrId: number;
  runId: number;
  stageNounSingular?: string;
  stageNounPlural?: string;
  onSuccess: () => void;
}

const ProposalGenerationStageApprovalWrapper = ({
  open,
  onClose,
  stages,
  reviewReportSdrId,
  runId,
  stageNounSingular,
  stageNounPlural,
  onSuccess,
}: ProposalGenerationStageApprovalWrapperProps) => {
  const { mutate: createStages, isLoading: isApproving } = useCreateStages();

  const handleApproveStages = (approvedStages: string[]) => {
    if (isApproving) {
      return;
    }

    createStages(
      {
        runId,
        payload: {
          stages: approvedStages,
          review_report_sdr_id: reviewReportSdrId,
        },
      },
      {
        onSuccess: () => {
          NucToast({
            message: "Stages created successfully",
            type: "success",
          });
          onSuccess();
        },
        onError: (error) => {
          console.error("Failed to create stages:", error);
          NucToast({
            message: "Failed to create stages",
            type: "error",
          });
        },
      },
    );
  };

  return (
    <StageApprovalModal
      open={open}
      initialStages={stages}
      onApprove={handleApproveStages}
      onCancel={onClose}
      isApproving={isApproving}
      stageNounSingular={stageNounSingular}
      stageNounPlural={stageNounPlural}
    />
  );
};

export default ProposalGenerationChat;
